import { useState } from "react";
import {
  useNavigate,
  useNavigateByRedirectCookie
} from "hooks/useCustomNavigation";
import { useAppContext } from "context/app-context";
import { useShowSnackbar } from "hooks/useShowSnackbar";
import { useBrowserDeviceFingerprint } from "hooks/useBrowserDeviceFingerprint";
import { INTERNAL_PATHS } from "routes/paths";
import { setUserToken } from "services/auth";
import { differenceInDaysBeetweenTwoDates } from "utils/date";
import { signIn } from "./requests";

export const useLoginSubmit = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { deviceFingerprint } = useBrowserDeviceFingerprint();
  const { addNotification } = useShowSnackbar();
  const { getSessionInfo } = useAppContext();
  const navigate = useNavigate();
  const redirect = useNavigateByRedirectCookie();

  const getSessionInfoAndNavigate = async ({ route }) => {
    await getSessionInfo();

    // wait for GuardRouter redirect first then navigate here
    setTimeout(() => {
      navigate(route);
    }, 100);
  };

  const getSessionInfoAndRedirect = async ({ route }) => {
    await getSessionInfo();
    redirect({ defaultRoute: route });
  };

  const handleSignInResponse = async (response) => {
    if (response.verification_required) {
      const { patch_token } = response;
      setUserToken({ token: patch_token });
      await getSessionInfoAndNavigate({
        route: INTERNAL_PATHS.DEVICE_VERIFICATION_NEW
      });
    } else {
      const { access_token, token_expiration } = response;
      const daysToExpireToken = differenceInDaysBeetweenTwoDates({
        currentDate: new Date(),
        finishDate: new Date(token_expiration)
      });
      setUserToken({ token: access_token, days: daysToExpireToken });
      await getSessionInfoAndRedirect({
        route: INTERNAL_PATHS.HOME
      });
    }
  };

  const handleSignInErrorByStatusCode = ({ statusCode, messageFromServer }) => {
    switch (statusCode) {
      case 400:
        return messageFromServer;
      case 401:
        return "Invalid email or password.";
      case 429:
        return "Your account has been locked. For security reasons we request to change your password.";
      case 500:
      default:
        return "Authorization Failure.";
    }
  };

  const submitLogin = async ({
    email,
    password,
    shouldRemember,
    captchaToken
  }) => {
    try {
      setIsLoading(true);
      const response = await signIn({
        email,
        password,
        fingerprint: deviceFingerprint,
        shouldRemember,
        captchaToken
      });
      await handleSignInResponse(response);
    } catch (error) {
      const errorMessage = handleSignInErrorByStatusCode({
        statusCode: error.status,
        messageFromServer: error.response.data.error
      });
      addNotification({
        message: errorMessage,
        variant: "error",
        persist: true
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    submitLogin
  };
};
